/**
 * emailStore — central reactive state for the email UI.
 *
 * Built on @preact/signals. Public API composed from store/* slices.
 * CSR mode: JSON API + ActionCable (EmailJsonChannel).
 *
 * Signal groups:
 *   - Layout    : sidebar collapsed state, list width, active panel (mobile)
 *   - Data      : emails array, thread object, sidebar counts
 *   - List      : selected email ids, current thread id, loading flags
 *   - Search    : text, quick filter, sort, advanced filters
 *   - Thread    : thread loading flag, comment form visibility, audit log visibility
 *   - Compose   : open state, type, draft id, dirty flag
 *   - Sidebar   : active folder key, active mailbox id
 */

import { signal, computed, batch } from '@preact/signals'
import { subscribe as cableSubscribe } from '../../../lib/cable.js'
import { apiGet, apiPatch, apiPost, apiDelete, ackThreadOpened } from './api.js'
import { attachCore } from './core.js'
import { attachList } from './list.js'
import { attachThread } from './thread.js'
import { attachSidebar } from './sidebar.js'
import { attachCompose } from './compose.js'
import { attachCable } from './cable.js'
import { attachUrl } from './url.js'

export function createEmailStore({ i18n } = {}) {
  // ── Layout ───────────────────────────────────────────────────────────────

  const sidebarCollapsed = signal(
    localStorage.getItem('email_sidebar_collapsed') === 'true'
  )
  const listWidth = signal(
    parseInt(localStorage.getItem('email_list_width') || '320', 10)
  )
  // Mobile: 'sidebar' | 'list' | 'pane'
  const initialWidth = window.innerWidth
  const activePanel = signal(initialWidth >= 1400 ? null : 'list')
  const isMobileLayout = signal(initialWidth < 768)
  const isCompactNavLayout = signal(initialWidth >= 768 && initialWidth < 1400)

  // ── Data — email list ─────────────────────────────────────────────────────

  const emails = signal([])
  const emailsMeta = signal({ total_count: 0, page: 1, next_page: null, unread_count: 0 })
  const emailsLoading = signal(false)
  const emailsLoadingMore = signal(false)

  // ── Data — current thread ─────────────────────────────────────────────────

  const thread = signal(null)
  const threadLoading = signal(false)

  // ── Data — sidebar counts ─────────────────────────────────────────────────

  // Flat Record<string, number>: inbox, starred, archived, folder_<id>, tag_<id>, mailbox_<id>
  const sidebarCounts = signal({})
  const storageQuota = signal(null)

  // ── List & selection ─────────────────────────────────────────────────────

  const selectedIds     = signal([])
  const currentThreadId = signal(null)
  const listLoading     = signal(false)

  // ── Search & filter ──────────────────────────────────────────────────────

  const searchText      = signal('')
  const quickFilter     = signal('')
  const sortOrder       = signal('newest')
  const advancedFilters = signal({})
  const filtersOpen     = signal(false)

  // ── Thread UI flags ───────────────────────────────────────────────────────

  const commentFormVisible = signal(false)
  const auditLogVisible    = signal(false)
  const expandedMessages   = signal({})
  const auditEvents        = signal([])
  const auditEventsLoading = signal(false)

  // ── Compose ──────────────────────────────────────────────────────────────

  const composeOpen  = signal(false)
  const composeType  = signal('new')   // 'new' | 'reply' | 'reply_all' | 'forward'
  const draftId      = signal(null)
  const composeDirty = signal(false)
  const composeOriginalEmailId = signal(null)
  const composeDraftData = signal(null)
  // Async SES confirm via EmailJsonChannel email_sent / email_send_failed
  const pendingSendEmailId = signal(null)
  const composeSendOutcome = signal(null) // { type, email_id } | null

  // ── Sidebar ──────────────────────────────────────────────────────────────

  const activeFolder  = signal('inbox')
  const activeMailbox = signal(null)   // mailbox id or null (all)
  const mailboxes        = signal([])
  const emailFolders  = signal([])
  const emailTags     = signal([])
  const peopleWithUser = signal([])
  const canEdit       = signal(false)
  const canImapImport = signal(false)
  // Set when ActionCable connects — used for optimistic comment author chips.
  const currentPersonId = signal(null)

  // ── Filter rules panel ───────────────────────────────────────────────────

  const rulesOpen = signal(false)
  const rulesMailboxId = signal(null)
  const rules = signal([])
  const rulesLoading = signal(false)
  const rulesEditingId = signal(null) // null = list, 'new' | number = editor

  // Task create / link from email
  const taskCreateOpen = signal(false)
  const taskCreateEmailId = signal(null)
  const taskLinkPickerOpen = signal(false)
  const taskLinkEmailId = signal(null)

  // Popup / deep-link: reading pane only (no sidebar, folders, or list)
  const threadOnlyView = signal(false)

  // ── Computed ─────────────────────────────────────────────────────────────

  const hasSelection    = computed(() => selectedIds.value.length > 0)
  const selectionCount  = computed(() => selectedIds.value.length)
  const isComposing     = computed(() => composeOpen.value)
  const hasActiveThread = computed(() => currentThreadId.value !== null)
  const isRulesOpen     = computed(() => rulesOpen.value)
  const isThreadOnlyView = computed(() => threadOnlyView.value)

  // Shared context for store slices
  const ctx = {
    i18n,
    batch,
    cableSubscribe,
    apiGet,
    apiPatch,
    apiPost,
    apiDelete,
    ackThreadOpened,

    sidebarCollapsed,
    listWidth,
    activePanel,
    isMobileLayout,
    isCompactNavLayout,
    emails,
    emailsMeta,
    emailsLoading,
    emailsLoadingMore,
    thread,
    threadLoading,
    sidebarCounts,
    storageQuota,
    selectedIds,
    currentThreadId,
    listLoading,
    searchText,
    quickFilter,
    sortOrder,
    advancedFilters,
    filtersOpen,
    commentFormVisible,
    auditLogVisible,
    expandedMessages,
    auditEvents,
    auditEventsLoading,
    composeOpen,
    composeType,
    draftId,
    composeDirty,
    composeOriginalEmailId,
    composeDraftData,
    pendingSendEmailId,
    composeSendOutcome,
    activeFolder,
    activeMailbox,
    mailboxes,
    emailFolders,
    emailTags,
    peopleWithUser,
    currentPersonId,
    canEdit,
    canImapImport,
    rulesOpen,
    rulesMailboxId,
    rules,
    rulesLoading,
    rulesEditingId,
    taskCreateOpen,
    taskCreateEmailId,
    taskLinkPickerOpen,
    taskLinkEmailId,
    threadOnlyView,
    hasSelection,
    selectionCount,
    isComposing,
    hasActiveThread,
    isRulesOpen,
    isThreadOnlyView,
  }

  attachCore(ctx)
  attachList(ctx)
  attachThread(ctx)
  attachSidebar(ctx)
  attachCompose(ctx)
  attachUrl(ctx)
  attachCable(ctx)

  return {
    // Signals
    sidebarCollapsed,
    listWidth,
    activePanel,
    isMobileLayout,
    isCompactNavLayout,
    emails,
    emailsMeta,
    emailsLoading,
    emailsLoadingMore,
    thread,
    threadLoading,
    sidebarCounts,
    storageQuota,
    selectedIds,
    currentThreadId,
    listLoading,
    searchText,
    quickFilter,
    sortOrder,
    advancedFilters,
    filtersOpen,
    commentFormVisible,
    auditLogVisible,
    expandedMessages,
    auditEvents,
    auditEventsLoading,
    composeOpen,
    composeType,
    draftId,
    composeDirty,
    composeOriginalEmailId,
    composeDraftData,
    pendingSendEmailId,
    composeSendOutcome,
    activeFolder,
    activeMailbox,
    mailboxes,
    emailFolders,
    emailTags,
    peopleWithUser,
    currentPersonId,
    canEdit,
    canImapImport,
    threadOnlyView,
    rulesOpen,
    rulesMailboxId,
    rules,
    rulesLoading,
    rulesEditingId,
    taskCreateOpen,
    taskCreateEmailId,
    taskLinkPickerOpen,
    taskLinkEmailId,

    // Computed
    hasSelection,
    selectionCount,
    isComposing,
    hasActiveThread,
    isRulesOpen,
    isThreadOnlyView,

    i18n,

    // Layout
    toggleSidebar: ctx.toggleSidebar,
    showSidebarPanel: ctx.showSidebarPanel,
    showListPanel: ctx.showListPanel,
    showReadingPane: ctx.showReadingPane,
    setListWidth: ctx.setListWidth,
    setLayoutMode: ctx.setLayoutMode,
    setMobileLayout: ctx.setMobileLayout,
    usesStackedNav: ctx.usesStackedNav,

    // Fetch
    fetchEmails: ctx.fetchEmails,
    fetchMoreEmails: ctx.fetchMoreEmails,
    fetchThread: ctx.fetchThread,
    scheduleThreadPrefetch: ctx.scheduleThreadPrefetch,
    cancelThreadPrefetch: ctx.cancelThreadPrefetch,
    openInitialRouteFromUrl: ctx.openInitialRouteFromUrl,
    syncInboxUrl: ctx.syncInboxUrl,
    applyInboxUrlFromLocation: ctx.applyInboxUrlFromLocation,
    loadAuditEvents: ctx.loadAuditEvents,

    // List & selection
    selectThread: ctx.selectThread,
    openThreadWindow: ctx.openThreadWindow,
    threadLoaded: ctx.threadLoaded,
    toggleSelected: ctx.toggleSelected,
    selectAll: ctx.selectAll,
    clearSelection: ctx.clearSelection,
    setCurrentThread: ctx.setCurrentThread,
    clearCurrentThread: ctx.clearCurrentThread,
    focusNext: ctx.focusNext,
    focusPrev: ctx.focusPrev,

    // Search
    setSearchText: ctx.setSearchText,
    setQuickFilter: ctx.setQuickFilter,
    toggleQuickFilter: ctx.toggleQuickFilter,
    setSort: ctx.setSort,
    setAdvancedFilter: ctx.setAdvancedFilter,
    clearAllFilters: ctx.clearAllFilters,
    toggleFiltersPanel: ctx.toggleFiltersPanel,
    reloadList: ctx.reloadList,
    buildListUrl: ctx.buildListUrl,

    // Thread UI
    setThreadLoading: ctx.setThreadLoading,
    showCommentForm: ctx.showCommentForm,
    hideCommentForm: ctx.hideCommentForm,
    toggleAuditLog: ctx.toggleAuditLog,
    toggleMessageExpanded: ctx.toggleMessageExpanded,
    expandMessage: ctx.expandMessage,
    collapseMessage: ctx.collapseMessage,

    // Mutations
    updateEmailState: ctx.updateEmailState,
    changeEmailStatus: ctx.changeEmailStatus,
    assignResponsible: ctx.assignResponsible,
    moveToFolder: ctx.moveToFolder,
    restoreToInbox: ctx.restoreToInbox,
    toggleTag: ctx.toggleTag,
    createTag: ctx.createTag,
    deleteTag: ctx.deleteTag,
    renameTag: ctx.renameTag,
    createFolder: ctx.createFolder,
    renameFolder: ctx.renameFolder,
    deleteFolder: ctx.deleteFolder,
    bulkUpdate: ctx.bulkUpdate,
    postComment: ctx.postComment,
    deleteComment: ctx.deleteComment,
    unlinkTask: ctx.unlinkTask,
    applyTaskInfobar: ctx.applyTaskInfobar,
    fetchTaskForm: ctx.fetchTaskForm,
    fetchTaskTags: ctx.fetchTaskTags,
    fetchTaskPersonGroups: ctx.fetchTaskPersonGroups,
    createTaskFromEmail: ctx.createTaskFromEmail,
    searchLinkableTasks: ctx.searchLinkableTasks,
    linkTaskToEmail: ctx.linkTaskToEmail,
    openTaskCreateModal: ctx.openTaskCreateModal,
    closeTaskCreateModal: ctx.closeTaskCreateModal,
    openTaskLinkPicker: ctx.openTaskLinkPicker,
    closeTaskLinkPicker: ctx.closeTaskLinkPicker,

    // Compose
    openCompose: ctx.openCompose,
    openComposeFromDraft: ctx.openComposeFromDraft,
    closeCompose: ctx.closeCompose,
    setDraftId: ctx.setDraftId,
    markComposeDirty: ctx.markComposeDirty,
    setPendingSendEmailId: ctx.setPendingSendEmailId,
    clearComposeSendOutcome: ctx.clearComposeSendOutcome,
    markComposeClean: ctx.markComposeClean,
    upsertDraftInList: ctx.upsertDraftInList,
    removeDraftFromList: ctx.removeDraftFromList,

    // Filter rules
    openRules: ctx.openRules,
    closeRules: ctx.closeRules,
    setRulesMailbox: ctx.setRulesMailbox,
    fetchRules: ctx.fetchRules,
    startNewRule: ctx.startNewRule,
    startEditRule: ctx.startEditRule,
    cancelRuleEdit: ctx.cancelRuleEdit,
    saveRule: ctx.saveRule,
    toggleRule: ctx.toggleRule,
    deleteRule: ctx.deleteRule,
    reorderRules: ctx.reorderRules,

    // Sidebar
    setActiveFolder: ctx.setActiveFolder,
    setActiveMailbox: ctx.setActiveMailbox,

    // ActionCable
    connectActionCable: ctx.connectActionCable,
    disconnectActionCable: ctx.disconnectActionCable,

    // Bootstrap
    bootstrap: ctx.bootstrap,

    // Counts pull
    fetchSidebarCounts: ctx.fetchSidebarCounts,
    adjustSidebarCount: ctx._adjustCount,
  }
}
